<template>
  <div class="header">
    <div class="header-left">
      <el-icon class="collapse-btn" @click="toggleCollapse">
        <Fold v-if="!isCollapse" />
        <Expand v-if="isCollapse" />
      </el-icon>
    </div>

    <div class="header-right">
      <el-badge :value="uncompletedCount" :hidden="uncompletedCount === 0" class="todo-badge">
        <el-button size="small" @click="handleOpenTodo">待办</el-button>
      </el-badge>

      <el-button size="small" @click="showNoteDialog = true" class="note-btn">笔记</el-button>

      <template v-if="!isMobile">
        <el-dropdown @command="handleMenuCommand">
          <div class="user-info">
            <el-avatar :size="30" :src="userAvatar" />
            <span class="username">{{ userName }}</span>
            <el-icon class="arrow"><ArrowDown /></el-icon>
          </div>
          <template #dropdown>
            <el-dropdown-menu command="menu">
              <el-dropdown-item command="profile">👤 个人中心</el-dropdown-item>
              <!-- <el-dropdown-item command="change">🔄 切换账号</el-dropdown-item>
              <el-dropdown-item command="setting">⚙️ 系统设置</el-dropdown-item> -->
              <el-dropdown-item divided command="logout">🚪 退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>
    </div>
  </div>

  <!-- 个人中心弹窗 -->
  <ProfileDialog v-model="showProfileDialog" @profile-updated="refreshUserInfo" />

  <!-- 笔记弹窗 -->
  <NoteDialog v-model="showNoteDialog" />

  <!-- ============ 待办事项主弹窗 ============ -->
  <el-dialog v-model="showTodoDialog" :title="isMobile ? '' : '待办事项'" :fullscreen="isMobile" width="960px" top="50px" destroy-on-close :class="{ 'todo-dialog-mobile': isMobile }">
    <template #header v-if="isMobile">
      <div class="mobile-dialog-header">
        <span class="mobile-dialog-title">待办事项</span>
        <el-button text :icon="Close" @click="showTodoDialog = false" />
      </div>
    </template>

    <div class="todo-layout" :class="{ 'is-mobile': isMobile }">
      <!-- 左侧日历 -->
      <div class="todo-calendar-panel">
        <el-calendar v-model="selectedDate" @change="onCalendarChange">
          <template #date-cell="{ data }">
            <div class="calendar-cell" :class="{
              'is-today': data.type === 'today',
              'is-other-month': data.type === 'prev-month' || data.type === 'next-month'
            }" @click="onDateClick(data.date)">
              <span class="cell-day">{{ data.day.split('-')[2] }}</span>
              <span v-if="dayCounts[data.day]" class="cell-count">{{ dayCounts[data.day] }}</span>
            </div>
          </template>
        </el-calendar>
      </div>

      <!-- 右侧待办列表 -->
      <div class="todo-content-panel">
        <!-- 筛选模式 -->
        <div class="todo-filter-bar">
          <el-radio-group v-model="filterMode" size="small" @change="onFilterModeChange" class="filter-mode-group">
            <el-radio-button value="date">按日期</el-radio-button>
            <el-radio-button value="uncompleted">未完成</el-radio-button>
            <el-radio-button value="completed">已完成</el-radio-button>
          </el-radio-group>
        </div>

        <!-- 分类标签 -->
        <el-tabs v-model="activeCategory" @tab-change="onCategoryChange" class="category-tabs">
          <el-tab-pane v-for="cat in categories" :key="cat.value" :label="cat.label" :name="cat.value" />
        </el-tabs>

        <!-- 日期标题 / 筛选标题 -->
        <div class="todo-date-header">
          <el-icon><Calendar /></el-icon>
          <span class="todo-date-text">{{ listTitle }}</span>
          <span class="todo-total">{{ todoTotal }} 项</span>
        </div>

        <!-- 待办列表 -->
        <div class="todo-items" v-if="todoList.length > 0">
          <div v-for="item in todoList" :key="item.id" class="todo-item" :class="'cat-' + (item.category || 'daily')">
            <el-checkbox v-model="item.is_completed" @change="handleToggle(item)" />
            <div class="todo-item-body">
              <div class="todo-item-top">
                <span class="todo-item-title" :class="{ completed: item.is_completed }">{{ item.title }}</span>
                <span class="category-tag" :style="{
                  background: getCategoryColor(item.category) + '18',
                  color: getCategoryColor(item.category),
                  borderColor: getCategoryColor(item.category) + '35'
                }">{{ getCategoryLabel(item.category) }}</span>
              </div>
              <div class="todo-item-desc" v-if="item.description">{{ item.description }}</div>
              <div class="todo-item-date" v-if="filterMode !== 'date' && item.due_date">
                <el-icon><Calendar /></el-icon>
                {{ item.due_date }}
              </div>
            </div>
            <div class="todo-item-actions">
              <el-tooltip content="编辑">
                <el-button link type="primary" :icon="Edit" size="small" @click="handleOpenEdit(item)" />
              </el-tooltip>
              <el-tooltip content="删除">
                <el-button link type="danger" :icon="Delete" size="small" @click="handleDelete(item)" />
              </el-tooltip>
            </div>
          </div>
        </div>

        <div v-else class="todo-empty">
          <el-empty :image-size="isMobile ? 60 : 80" :description="emptyText" />
        </div>

        <!-- 添加待办 -->
        <div class="todo-add-bar">
          <el-input v-model="newTodoTitle" placeholder="输入新待办事项..." clearable @keyup.enter="handleAddTodo" />
          <el-select v-model="newTodoCategory" style="width: 105px">
            <el-option v-for="cat in categories.slice(1)" :key="cat.value" :label="cat.label" :value="cat.value">
              <span class="option-dot" :style="{ background: cat.color }"></span>
              <span>{{ cat.label }}</span>
            </el-option>
          </el-select>
          <el-button type="primary" @click="handleAddTodo">添加</el-button>
        </div>
      </div>
    </div>
  </el-dialog>

  <!-- ============ 编辑待办弹窗 ============ -->
  <el-dialog v-model="editDialogVisible" title="编辑待办事项" width="480px" top="25vh" :fullscreen="isMobile && isMobileEdit" destroy-on-close>
    <el-form :model="editingTodo" label-width="60px">
      <el-form-item label="标题">
        <el-input v-model="editingTodo.title" placeholder="请输入标题" />
      </el-form-item>
      <el-form-item label="描述">
        <el-input v-model="editingTodo.description" type="textarea" :rows="3" placeholder="请输入描述（可选）" />
      </el-form-item>
      <el-form-item label="分类">
        <el-select v-model="editingTodo.category">
          <el-option v-for="cat in categories.slice(1)" :key="cat.value" :label="cat.label" :value="cat.value">
            <span class="option-dot" :style="{ background: cat.color }"></span>
            <span>{{ cat.label }}</span>
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="日期">
        <el-date-picker v-model="editingTodo.due_date" type="date" placeholder="选择日期" value-format="YYYY-MM-DD" style="width: 100%" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="editDialogVisible = false">取消</el-button>
      <el-button type="primary" @click="handleSaveEdit">保存</el-button>
    </template>
  </el-dialog>
</template>

<script>
/* eslint-disable */
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Fold, Expand, ArrowDown, Calendar, Edit, Delete, Close } from '@element-plus/icons-vue'
import { getTodoList, addTodoItem, updateTodoItem, deleteTodoItem, getUncompletedCount, getTodoMonthCounts } from '@/api/todoApi'
import ProfileDialog from './ProfileDialog.vue'
import NoteDialog from './NoteDialog.vue'

export default {
  name: 'HeaderBackend',
  components: { Fold, Expand, ArrowDown, Calendar, Edit, Delete, Close, ProfileDialog, NoteDialog },
  emits: ['toggle-sidebar'],
  setup() {
    const router = useRouter()
    const nowTime = ref('')
    const isCollapse = ref(false)
    const isMobile = ref(false)
    const showProfileDialog = ref(false)
    const showNoteDialog = ref(false)
    let timer = null

    const userInfo = computed(() => {
      try {
        return JSON.parse(localStorage.getItem('userInfo') || '{}')
      } catch { return {} }
    })
    const userName = computed(() => userInfo.value.real_name || userInfo.value.username || '管理员')
    const userAvatar = computed(() => userInfo.value.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png')

    const refreshUserInfo = () => {
      // triggers computed re-evaluation by changing the ref via localStorage
    }

    // ─── 待办分类配置 ───
    const categories = [
      { value: 'all', label: '全部', color: '' },
      { value: 'daily', label: '日常', color: '#909399' },
      { value: 'work', label: '工作', color: '#409EFF' },
      { value: 'urgent', label: '紧急', color: '#F56C6C' },
      { value: 'sale', label: '销售', color: '#E6A23C' },
      { value: 'purchase', label: '采购', color: '#67C23A' },
      { value: 'finance', label: '财务', color: '#9B59B6' },
      { value: 'production', label: '生产', color: '#00BCD4' }
    ]

    const getCategoryLabel = (val) => {
      const cat = categories.find(c => c.value === val)
      return cat ? cat.label : val
    }

    const getCategoryColor = (val) => {
      const cat = categories.find(c => c.value === val)
      return cat ? cat.color : '#909399'
    }

    // ─── 状态 ───
    const showTodoDialog = ref(false)
    const selectedDate = ref(new Date())
    const dayCounts = ref({})
    const filterMode = ref('date')
    const activeCategory = ref('all')
    const todoList = ref([])
    const todoTotal = ref(0)
    const newTodoTitle = ref('')
    const newTodoCategory = ref('daily')
    const editDialogVisible = ref(false)
    const isMobileEdit = ref(false)
    const editingTodo = ref({ id: null, title: '', description: '', category: 'daily', due_date: '' })
    const uncompletedCount = ref(0)

    const formattedDate = (date) => {
      if (!date) return ''
      const d = date instanceof Date ? date : new Date(date)
      if (isNaN(d.getTime())) return ''
      const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      return d.getFullYear() + '年' + (d.getMonth() + 1) + '月' + d.getDate() + '日 ' + weekDays[d.getDay()]
    }

    const listTitle = computed(() => {
      if (filterMode.value === 'date') return formattedDate(selectedDate.value)
      if (filterMode.value === 'uncompleted') return '所有未完成事项'
      if (filterMode.value === 'completed') return '所有已完成事项'
      return ''
    })

    const emptyText = computed(() => {
      if (filterMode.value === 'date') return '当天没有待办事项'
      if (filterMode.value === 'uncompleted') return '没有未完成的事项'
      if (filterMode.value === 'completed') return '没有已完成的事项'
      return '暂无待办事项'
    })

    const toDateStr = (date) => {
      if (!date) return ''
      const d = date instanceof Date ? date : new Date(date)
      if (isNaN(d.getTime())) return ''
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    }

    // ─── 数据获取 ───
    const fetchTodoList = async () => {
      try {
        const params = {}
        if (activeCategory.value !== 'all') {
          params.category = activeCategory.value
        }
        if (filterMode.value === 'date') {
          params.due_date = toDateStr(selectedDate.value)
        } else if (filterMode.value === 'uncompleted') {
          params.is_completed = 0
        } else if (filterMode.value === 'completed') {
          params.is_completed = 1
        }
        const res = await getTodoList(params)
        if (res.code === 200) {
          todoList.value = (res.data.list || []).map(item => ({
            ...item,
            is_completed: !!item.is_completed,
            due_date: item.due_date ? String(item.due_date).split('T')[0] : ''
          }))
          todoTotal.value = res.data.total
        }
      } catch (e) {
        console.error(e)
      }
    }

    const fetchMonthCounts = async (yearMonth) => {
      try {
        const res = await getTodoMonthCounts(yearMonth)
        if (res.code === 200) {
          dayCounts.value = res.data || {}
        }
      } catch (e) {
        console.error(e)
      }
    }

    const fetchUncompletedCount = async () => {
      try {
        const res = await getUncompletedCount()
        if (res.code === 200) {
          uncompletedCount.value = res.data
        }
      } catch (e) {
        console.error(e)
      }
    }

    // ─── 事件处理 ───
    const handleOpenTodo = () => {
      filterMode.value = 'date'
      selectedDate.value = new Date()
      showTodoDialog.value = true
      fetchTodoList()
      const now = new Date()
      const ym = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0')
      fetchMonthCounts(ym)
    }

    const onDateClick = (date) => {
      selectedDate.value = date
      if (filterMode.value !== 'date') {
        filterMode.value = 'date'
      }
      fetchTodoList()
    }

    const onCalendarChange = (range) => {
      if (!range || !range[0]) return
      const ym = range[0].getFullYear() + '-' + String(range[0].getMonth() + 1).padStart(2, '0')
      fetchMonthCounts(ym)
    }

    const onFilterModeChange = () => {
      fetchTodoList()
    }

    const onCategoryChange = () => {
      fetchTodoList()
    }

    const handleAddTodo = async () => {
      const title = newTodoTitle.value.trim()
      if (!title) return
      const res = await addTodoItem({
        title,
        category: newTodoCategory.value,
        due_date: toDateStr(selectedDate.value)
      })
      if (res.code === 200) {
        ElMessage.success('添加成功')
        newTodoTitle.value = ''
        await fetchTodoList()
        await fetchUncompletedCount()
        const ym = selectedDate.value.getFullYear() + '-' + String(selectedDate.value.getMonth() + 1).padStart(2, '0')
        await fetchMonthCounts(ym)
      }
    }

    const handleToggle = async (item) => {
      const res = await updateTodoItem({
        id: item.id,
        title: item.title,
        description: item.description || '',
        category: item.category || 'daily',
        due_date: item.due_date || toDateStr(selectedDate.value),
        is_completed: item.is_completed ? 1 : 0
      })
      if (res.code !== 200) {
        item.is_completed = !item.is_completed
      } else {
        await fetchUncompletedCount()
      }
    }

    const handleOpenEdit = (item) => {
      isMobileEdit.value = isMobile.value
      editingTodo.value = {
        id: item.id,
        title: item.title,
        description: item.description || '',
        category: item.category || 'daily',
        due_date: item.due_date || ''
      }
      editDialogVisible.value = true
    }

    const handleSaveEdit = async () => {
      const t = editingTodo.value.title.trim()
      if (!t) {
        ElMessage.warning('标题不能为空')
        return
      }
      const res = await updateTodoItem({
        id: editingTodo.value.id,
        title: t,
        description: editingTodo.value.description || '',
        category: editingTodo.value.category || 'daily',
        due_date: editingTodo.value.due_date || toDateStr(selectedDate.value),
        is_completed: editingTodo.value.is_completed ? 1 : 0
      })
      if (res.code === 200) {
        ElMessage.success('修改成功')
        editDialogVisible.value = false
        await fetchTodoList()
        await fetchUncompletedCount()
        const ym = selectedDate.value.getFullYear() + '-' + String(selectedDate.value.getMonth() + 1).padStart(2, '0')
        await fetchMonthCounts(ym)
      }
    }

    const handleDelete = async (item) => {
      try {
        await ElMessageBox.confirm('确定删除此待办事项？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const res = await deleteTodoItem(item.id)
        if (res.code === 200) {
          ElMessage.success('删除成功')
          await fetchTodoList()
          await fetchUncompletedCount()
          const ym = selectedDate.value.getFullYear() + '-' + String(selectedDate.value.getMonth() + 1).padStart(2, '0')
          await fetchMonthCounts(ym)
        }
      } catch (e) {
        // cancelled
      }
    }

    // ─── 通用 ───
    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    const updateTime = () => {
      const date = new Date()
      const y = date.getFullYear()
      const m = (date.getMonth() + 1).toString().padStart(2, '0')
      const d = date.getDate().toString().padStart(2, '0')
      const h = date.getHours().toString().padStart(2, '0')
      const mm = date.getMinutes().toString().padStart(2, '0')
      const s = date.getSeconds().toString().padStart(2, '0')
      nowTime.value = `${y}-${m}-${d} ${h}:${mm}:${s}`
    }

    onMounted(() => {
      updateTime()
      timer = setInterval(updateTime, 1000)
      checkMobile()
      window.addEventListener('resize', checkMobile)
      fetchUncompletedCount()
    })

    onUnmounted(() => {
      clearInterval(timer)
      window.removeEventListener('resize', checkMobile)
    })

    return {
      nowTime, isMobile, isCollapse, showProfileDialog, showNoteDialog,
      showTodoDialog, selectedDate, dayCounts, filterMode, activeCategory, categories,
      todoList, todoTotal, newTodoTitle, newTodoCategory,
      editDialogVisible, isMobileEdit, editingTodo, uncompletedCount,
      listTitle, emptyText, userAvatar, userName,
      getCategoryLabel, getCategoryColor,
      handleOpenTodo, onDateClick, onCalendarChange,
      onFilterModeChange, onCategoryChange,
      handleAddTodo, handleToggle, handleOpenEdit, handleSaveEdit, handleDelete,
      refreshUserInfo,
      Close
    }
  },
  beforeCreate() {
    this.handleMenuCommand = (cmd) => {
      if (cmd === 'logout') {
        ElMessage.success('退出成功')
        localStorage.removeItem('token')
        this.$router.push('/AdminLogin')
      } else if (cmd === 'change') {
        ElMessage.info('切换账号功能')
        this.$router.push('/AdminLogin')
      } else if (cmd === 'profile') {
        this.showProfileDialog = true
      } else if (cmd === 'setting') {
        ElMessage.success('系统设置')
      }
    }

    this.toggleCollapse = () => {
      if (window.innerWidth <= 768) {
        this.$emit('toggle-sidebar')
        return
      }
      const side = document.querySelector('.sidebar-container')
      if (side) {
        const vm = side.__vue__ || side._vnode?.component?.exposed
        if (vm && typeof vm.isCollapse !== 'undefined') {
          vm.isCollapse = !vm.isCollapse
          this.isCollapse = vm.isCollapse
        }
      }
    }
  }
}
</script>

<style scoped>
.header {
  height: 60px;
  background: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.collapse-btn {
  font-size: 19px;
  cursor: pointer;
  color: #555;
}

.breadcrumb {
  font-size: 14px;
  color: #777;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.time-box {
  font-size: 14px;
  color: #666;
  padding-right: 10px;
  border-right: 1px solid #eee;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
}

.user-info:hover {
  background: #f5f7fa;
}

.username {
  font-size: 14px;
  color: #333;
}

.arrow {
  font-size: 12px;
  color: #999;
}

.todo-badge {
  margin-right: 4px;
}

/* ─── 移动端弹窗头部 ─── */
.mobile-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  width: 100%;
}

.mobile-dialog-title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
}

/* ─── 布局 ─── */
.todo-layout {
  display: flex;
  gap: 0;
  min-height: 480px;
}

.todo-layout.is-mobile {
  flex-direction: column;
  min-height: 0;
}

/* ─── 日历面板 ─── */
.todo-calendar-panel {
  width: 385px;
  flex-shrink: 0;
  border-right: 1px solid #f0f0f0;
  padding-right: 0;
  margin: -10px 0 -10px -10px;
}

.todo-layout.is-mobile .todo-calendar-panel {
  width: 100%;
  border-right: none;
  border-bottom: 1px solid #f0f0f0;
  margin: 0;
  margin-bottom: 8px;
}

:deep(.el-calendar) {
  border: none;
}

:deep(.el-calendar__header) {
  padding: 8px 12px;
}

:deep(.el-calendar__title) {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

:deep(.el-calendar__button-group) {
  display: flex;
  gap: 4px;
}

:deep(.el-calendar__button-group .el-button-group) {
  display: flex;
}

:deep(.el-calendar__button-group .el-button) {
  padding: 4px 10px;
  font-size: 12px;
}

:deep(.el-calendar-table) {
  border-collapse: collapse;
}

:deep(.el-calendar-table thead th) {
  padding: 6px 0;
  font-size: 12px;
  font-weight: 500;
  color: #606266;
  background: #fafafa;
  border: 1px solid #ebeef5;
}

:deep(.el-calendar-table td) {
  border: 1px solid #ebeef5;
  padding: 0;
  vertical-align: top;
  cursor: pointer;
  transition: background 0.15s;
}

:deep(.el-calendar-table td:hover) {
  background: #f5f7fa;
}

:deep(.el-calendar-table td.is-today) {
  background: #ecf5ff;
}

:deep(.el-calendar-table td.is-selected) {
  background: #ecf5ff;
}

:deep(.el-calendar-table .el-calendar-day) {
  height: 64px;
  padding: 2px;
}

.todo-layout.is-mobile :deep(.el-calendar-table .el-calendar-day) {
  height: 48px;
}

/* 日历单元格 */
.calendar-cell {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: 2px 4px;
  position: relative;
}

.calendar-cell.is-other-month .cell-day {
  color: #c0c4cc;
}

.cell-day {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  line-height: 1;
}

.todo-layout.is-mobile .cell-day {
  font-size: 12px;
}

.cell-count {
  font-size: 10px;
  background: #F56C6C;
  color: #fff;
  border-radius: 10px;
  padding: 0 5px;
  line-height: 16px;
  min-width: 16px;
  text-align: center;
  align-self: flex-end;
  margin-top: auto;
}

/* ─── 内容面板 ─── */
.todo-content-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-left: 16px;
}

.todo-layout.is-mobile .todo-content-panel {
  padding-left: 0;
}

/* ─── 筛选栏 ─── */
.todo-filter-bar {
  margin-bottom: 8px;
}

.filter-mode-group {
  display: flex;
}

.filter-mode-group :deep(.el-radio-button__inner) {
  font-size: 12px;
  padding: 5px 12px;
}

/* ─── 分类标签 ─── */
.category-tabs {
  margin-bottom: 0;
}

:deep(.category-tabs .el-tabs__header) {
  margin: 0 0 8px;
}

:deep(.category-tabs .el-tabs__nav-wrap::after) {
  height: 1px;
}

:deep(.category-tabs .el-tabs__item) {
  font-size: 13px;
  padding: 0 12px;
  height: 32px;
  line-height: 32px;
}

.todo-layout.is-mobile :deep(.category-tabs .el-tabs__item) {
  font-size: 12px;
  padding: 0 8px;
}

/* ─── 日期标题 ─── */
.todo-date-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
}

.todo-date-text {
  font-weight: 500;
  color: #303133;
}

.todo-total {
  margin-left: auto;
  color: #909399;
  font-size: 12px;
}

/* ─── 待办列表 ─── */
.todo-items {
  flex: 1;
  overflow-y: auto;
  max-height: 340px;
}

.todo-layout.is-mobile .todo-items {
  max-height: none;
}

.todo-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 8px;
  border-radius: 6px;
  border-left: 3px solid transparent;
  margin-bottom: 4px;
  transition: background 0.15s;
}

.todo-item:hover {
  background: #f5f7fa;
}

.todo-item.cat-daily { border-left-color: #909399; }
.todo-item.cat-work { border-left-color: #409EFF; }
.todo-item.cat-urgent { border-left-color: #F56C6C; }
.todo-item.cat-sale { border-left-color: #E6A23C; }
.todo-item.cat-purchase { border-left-color: #67C23A; }
.todo-item.cat-finance { border-left-color: #9B59B6; }
.todo-item.cat-production { border-left-color: #00BCD4; }

.todo-item-body {
  flex: 1;
  min-width: 0;
}

.todo-item-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.todo-item-title {
  font-size: 14px;
  color: #303133;
  word-break: break-all;
  flex: 1;
}

.todo-item-title.completed {
  text-decoration: line-through;
  color: #c0c4cc;
}

.category-tag {
  flex-shrink: 0;
  font-size: 11px;
  padding: 0 8px;
  line-height: 20px;
  border-radius: 4px;
  border: 1px solid;
  white-space: nowrap;
}

.todo-item-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.todo-item-date {
  font-size: 11px;
  color: #bbb;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 3px;
}

.todo-item-date .el-icon {
  font-size: 11px;
}

.todo-item-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.todo-item:hover .todo-item-actions {
  opacity: 1;
}

.todo-layout.is-mobile .todo-item-actions {
  opacity: 1;
}

/* ─── 空状态 ─── */
.todo-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ─── 添加栏 ─── */
.todo-add-bar {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
}

.todo-add-bar .el-input {
  flex: 1;
}

.option-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

/* ─── 桌面端响应式 ─── */
@media (max-width: 1024px) {
  .todo-layout {
    flex-direction: column;
  }
  .todo-calendar-panel {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
    margin: 0;
    margin-bottom: 12px;
  }
  .todo-content-panel {
    padding-left: 0;
  }
  :deep(.el-calendar-table .el-calendar-day) {
    height: 48px;
  }
}
</style>
